import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { EditorField } from '@/config/ecoBuilder/types';
import { usePageContext } from '@/components/ecoBuilder/renderer/PageContext';
import { ChevronDown, ShieldCheck, Truck, Clock, RefreshCw, BadgeCheck, HelpCircle, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { RichTextRenderer } from '@/components/ecoBuilder/atoms/RichTextRenderer';

// 1. The Interface
export interface ObjectionKillerFAQProps {
    items?: {
        id?: string;
        question: string;
        answer: string;
        icon?: string;
    }[];
    variant?: 'classic_accordion' | 'grid_cards' | 'side_panel' | 'highlight_hero';
    defaultOpen?: boolean;
    title?: string;
    description?: string;
    textColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    iconColor?: string;
    cardBackgroundColor?: string;
    itemSpacing?: 'compact' | 'normal' | 'loose';
}

// 2. The Configuration Schema
export const ObjectionKillerFAQSchema: EditorField[] = [
    {
        key: 'variant',
        label: 'نط العرض (Layout)',
        type: 'select',
        options: [
            { value: 'classic_accordion', label: 'قائمة تقليدية (Classic)' },
            { value: 'grid_cards', label: 'شبكة بطاقات (Grid Cards)' },
            { value: 'side_panel', label: 'لوحة جانبية (Side Panel)' },
            { value: 'highlight_hero', label: 'بطاقة مميزة (Highlight Hero)' }
        ],
        defaultValue: 'highlight_hero'
    },
    {
        key: 'items',
        label: 'الأسئلة والأجوبة',
        type: 'array',
        arrayConfig: {
            itemLabel: 'سؤال',
            schema: [
                { key: 'question', label: 'السؤال', type: 'text', defaultValue: 'سؤال جديد؟' },
                { key: 'answer', label: 'الإجابة', type: 'textarea', defaultValue: 'إجابة السؤال...' },
                {
                    key: 'icon',
                    label: 'أيقونة',
                    type: 'select',
                    options: [
                        { value: 'truck', label: 'شحن (Truck)' },
                        { value: 'shield', label: 'حماية (Shield)' },
                        { value: 'clock', label: 'وقت (Clock)' },
                        { value: 'refresh', label: 'استبدال (Refresh)' },
                        { value: 'badge', label: 'شارة (Badge)' },
                        { value: 'help', label: 'سؤال (Help)' }
                    ],
                    defaultValue: 'badge'
                }
            ]
        },
        defaultValue: [
            { id: 'faq_1', question: 'متى سأتوصل بالمنتج؟', answer: 'نقوم بالتوصيل خلال 24-48 ساعة لجميع الولايات.', icon: 'truck' },
            { id: 'faq_2', question: 'هل يمكنني الدفع عند الاستلام؟', answer: 'نعم، الدفع يكون نقداً فقط عند استلام المنتج.', icon: 'shield' },
            { id: 'faq_3', question: 'ماذا لو لم يعجبني المنتج؟', answer: 'نوفر ضمان استبدال مجاني في حالة وجود أي عيب مصنعي.', icon: 'refresh' },
        ],
        description: 'أضف الأسئلة التي تتردد عند العملاء'
    },
    {
        key: 'title',
        label: 'عنوان القسم',
        type: 'text',
        defaultValue: 'أسئلة شائعة'
    },
    {
        key: 'description',
        label: 'وصف فرعي',
        type: 'richtext',
        defaultValue: ''
    },
    {
        key: 'defaultOpen',
        label: 'فتح السؤال الأول تلقائياً',
        type: 'boolean',
        defaultValue: true
    },
    {
        key: 'itemSpacing',
        label: 'المسافة بين العناصر',
        type: 'select',
        options: [
            { value: 'compact', label: 'مضغوط (Compact)' },
            { value: 'normal', label: 'متوسط (Normal)' },
            { value: 'loose', label: 'متباعد (Loose)' }
        ],
        defaultValue: 'normal'
    },
    {
        key: 'cardBackgroundColor',
        label: 'لون خلفية البطاقات',
        type: 'color',
        defaultValue: '#ffffff',
    },
    {
        key: 'textColor',
        label: 'لون النصوص',
        type: 'color',
        defaultValue: '#09090b',
    },
    {
        key: 'accentColor',
        label: 'لون التمييز (الأساسي - Highlight)',
        type: 'color',
        description: 'اتركه فارغاً ليأخذ اللون الأساسي من المتجر',
    },
    {
        key: 'iconColor',
        label: 'لون الأيقونات (الثانوي)',
        type: 'color',
        description: 'اتركه فارغاً ليأخذ اللون الثانوي من المتجر',
    },
    {
        key: 'backgroundColor',
        label: 'لون خلفية القسم',
        type: 'color',
        defaultValue: 'transparent',
    }
];

export const ObjectionKillerFAQDefaultProps: Partial<ObjectionKillerFAQProps> = {
    variant: 'highlight_hero',
    title: 'أسئلة يتكرر طرحها',
    description: '',
    defaultOpen: true,
    itemSpacing: 'normal',
    textColor: '#09090b',
    cardBackgroundColor: '#ffffff',
    backgroundColor: 'transparent',
    items: [
        { question: 'متى سأتوصل بالمنتج؟', answer: 'نقوم بالتوصيل خلال 24-48 ساعة لجميع الولايات.', icon: 'truck' },
        { question: 'هل يمكنني الدفع عند الاستلام؟', answer: 'نعم، الدفع يكون نقداً فقط عند استلام المنتج.', icon: 'shield' },
        { question: 'ماذا لو لم يعجبني المنتج؟', answer: 'نوفر ضمان استبدال مجاني في حالة وجود أي عيب مصنعي.', icon: 'refresh' },
    ]
};

// Internal Helper to render icon
const IconRenderer = ({ name, className }: { name?: string, className?: string }) => {
    if (!name) return <BadgeCheck className={className} />;

    switch (name.toLowerCase()) {
        case 'truck': return <Truck className={className} />;
        case 'shield': return <ShieldCheck className={className} />;
        case 'clock': return <Clock className={className} />;
        case 'refresh': return <RefreshCw className={className} />;
        case 'help': return <HelpCircle className={className} />;
        default: return <BadgeCheck className={className} />;
    }
};
// 3. The Component Implementation
export const ObjectionKillerFAQ: React.FC<ObjectionKillerFAQProps> = ({
    items = [],
    variant = 'highlight_hero',
    defaultOpen = true,
    title,
    description,
    textColor,
    accentColor,
    iconColor,
    cardBackgroundColor = '#ffffff',
    backgroundColor = 'transparent',
    itemSpacing = 'normal',
    style,
    className
}) => {
    const { themeSettings } = usePageContext();

    // Store Settings (Highest Priority for Defaults)
    const storePrimaryColor = themeSettings?.primaryColor;
    const storeSecondaryColor = themeSettings?.secondaryColor;

    const defaultPrimary = '#7c3aed'; // Purple-600 like in screenshot
    const defaultSecondary = '#10b981'; // Emerald-500 like in screenshot

    // Logic: 
    // 1. Component Prop (Customizer Override)
    // 2. Store Setting (Database/Theme)
    // 3. System Fallback

    const resolvedPrimary = storePrimaryColor || defaultPrimary;
    const resolvedSecondary = storeSecondaryColor || defaultSecondary;

    const finalTextColor = (!textColor || textColor === '#09090b') ? (themeSettings?.textColor || '#09090b') : textColor;

    // Active/Highlight -> Defaults to Primary
    const finalAccentColor = accentColor || resolvedPrimary;

    // Inactive Icons -> Defaults to Secondary (per latest request request), fallback to Primary if needed
    // The user mentioned "Icons ... follow Primary and Secondary in order". 
    // Let's set Icons to use Secondary by default to distinguish them, or Primary if Secondary feels wrong.
    // Given the complaint about "Yellow" (old Secondary?), and the screenshot showing Mint Secondary.
    // I will default Icons to Secondary, but allow Override.
    const finalIconColor = iconColor || resolvedSecondary;

    const [openIndex, setOpenIndex] = useState<number | null>(defaultOpen ? 0 : null);

    const toggle = (idx: number) => {
        setOpenIndex(openIndex === idx ? null : idx);
    };

    const getSpacingClass = () => {
        switch (itemSpacing) {
            case 'compact': return 'space-y-2';
            case 'loose': return 'space-y-6';
            default: return 'space-y-4';
        }
    };

    // --- RENDERERS FOR VARIANTS ---

    // 1. Classic Accordion (Clean & Minimal)
    const renderClassicAccordion = () => (
        <div className={cn("max-w-3xl mx-auto", getSpacingClass())}>
            {items.map((item, idx) => {
                const isOpen = openIndex === idx;
                return (
                    <div
                        key={idx}
                        className="border-b border-black/5"
                        style={{ borderBottomColor: isOpen ? 'transparent' : 'rgba(0,0,0,0.1)' }}
                    >
                        <button
                            onClick={() => toggle(idx)}
                            className="w-full flex items-center justify-between py-4 text-right focus:outline-none group rounded-lg transition-colors hover:bg-black/5 px-2"
                        >
                            <div className="flex items-center gap-4">
                                <span style={{ color: isOpen ? finalAccentColor : finalIconColor }} className="transition-colors">
                                    <IconRenderer name={item.icon} className="w-6 h-6" />
                                </span>
                                <RichTextRenderer
                                    html={item.question}
                                    className="font-bold text-lg"
                                    style={{ color: isOpen ? finalAccentColor : finalTextColor }}
                                    inline
                                />
                            </div>
                            <ChevronDown
                                className={cn("w-5 h-5 transition-transform duration-300", isOpen && "rotate-180")}
                                style={{ color: isOpen ? finalAccentColor : finalIconColor }}
                            />
                        </button>
                        <AnimatePresence>
                            {isOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25 }}
                                    className="overflow-hidden"
                                >
                                    <div className="pb-6 pr-12 pl-4">
                                        <RichTextRenderer html={item.answer} className="leading-relaxed opacity-90" style={{ color: finalTextColor }} />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );

    // 2. Grid Cards (Boxed)
    const renderGridCards = () => (
        <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-4", itemSpacing === 'loose' ? 'gap-6' : 'gap-4')}>
            {items.map((item, idx) => {
                const isOpen = openIndex === idx;
                return (
                    <div
                        key={idx}
                        className={cn("rounded-2xl border border-transparent shadow-sm overflow-hidden transition-all duration-300", isOpen ? "ring-2" : "hover:shadow-md")}
                        style={{
                            backgroundColor: cardBackgroundColor,
                            boxShadow: isOpen ? `0 4px 12px ${finalAccentColor}20` : undefined,
                            borderColor: isOpen ? finalAccentColor : 'rgba(0,0,0,0.05)',
                        }}
                    >
                        <button
                            onClick={() => toggle(idx)}
                            className="w-full flex items-start justify-between p-5 text-right focus:outline-none"
                        >
                            <div className="flex gap-3">
                                <span
                                    className="p-2 rounded-full shrink-0"
                                    style={{ backgroundColor: isOpen ? `${finalAccentColor}15` : 'rgba(0,0,0,0.05)', color: isOpen ? finalAccentColor : finalIconColor }}
                                >
                                    <IconRenderer name={item.icon} className="w-5 h-5" />
                                </span>
                                <div>
                                    <RichTextRenderer
                                        html={item.question}
                                        className="font-bold text-lg mb-1"
                                        style={{ color: finalTextColor }}
                                        inline
                                    />
                                    {/* Small preview if closed ? No, just clean */}
                                </div>
                            </div>
                            <ChevronDown
                                className={cn("w-5 h-5 mt-2 transition-transform duration-300", isOpen && "rotate-180")}
                                style={{ color: finalIconColor }}
                            />
                        </button>
                        <AnimatePresence>
                            {isOpen && (
                                <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: "auto" }}
                                    exit={{ height: 0 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-5 pb-5 pt-0 mr-[52px]">
                                        <RichTextRenderer html={item.answer} className="leading-relaxed opacity-80 text-sm md:text-base" style={{ color: finalTextColor }} />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );

    // 3. Side Panel (Desktop Split, Mobile Stack)
    const renderSidePanel = () => (
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start max-w-5xl mx-auto">
            {/* List Side */}
            <div className={cn("w-full md:w-1/2 flex flex-col", getSpacingClass())}>
                {items.map((item, idx) => {
                    const isOpen = openIndex === idx;
                    return (
                        <button
                            key={idx}
                            onClick={() => setOpenIndex(idx)}
                            className={cn(
                                "w-full flex items-center gap-4 text-right p-4 rounded-xl transition-all duration-200 border",
                                isOpen ? "shadow-md scale-[1.02]" : "hover:bg-black/5 hover:border-transparent border-transparent"
                            )}
                            style={{
                                backgroundColor: isOpen ? cardBackgroundColor : 'transparent',
                                borderColor: isOpen ? finalAccentColor : 'rgba(0,0,0,0.05)'
                            }}
                        >
                            <div
                                className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors")}
                                style={{
                                    backgroundColor: isOpen ? finalAccentColor : 'rgba(0,0,0,0.05)',
                                    color: isOpen ? '#ffffff' : finalIconColor
                                }}
                            >
                                <IconRenderer name={item.icon} className="w-5 h-5" />
                            </div>
                            <RichTextRenderer
                                html={item.question}
                                className="font-bold text-lg"
                                style={{ color: isOpen ? finalAccentColor : finalTextColor }}
                                inline
                            />
                            {isOpen && <ArrowLeft className="mr-auto w-5 h-5 md:hidden" style={{ color: finalAccentColor }} />}
                        </button>
                    );
                })}
            </div>

            {/* Content Side (Sticky on Desktop) */}
            <div className="w-full md:w-1/2 md:sticky md:top-24">
                <AnimatePresence mode="wait">
                    {openIndex !== null && items[openIndex] && (
                        <motion.div
                            key={openIndex}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3 }}
                            className="p-6 md:p-8 rounded-2xl shadow-lg border border-transparent"
                            style={{ backgroundColor: cardBackgroundColor, borderColor: `${finalAccentColor}20` }}
                        >
                            <div className="flex items-center gap-3 mb-4 opacity-50 text-sm font-semibold uppercase tracking-wider" style={{ color: finalAccentColor }}>
                                <IconRenderer name={items[openIndex].icon} className="w-4 h-4" />
                                <span>إجابة السؤال</span>
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: finalTextColor }}>
                                <RichTextRenderer html={items[openIndex].question} inline />
                            </h3>
                            <div className="w-12 h-1 rounded-full mb-6" style={{ backgroundColor: finalAccentColor }}></div>
                            <RichTextRenderer
                                html={items[openIndex].answer}
                                className="leading-loose text-lg opacity-90"
                                style={{ color: finalTextColor }}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );

    // 4. Highlight Hero (Active item is big)
    const renderHighlightHero = () => (
        <div className="max-w-4xl mx-auto space-y-6">
            {items.map((item, idx) => {
                const isOpen = openIndex === idx;
                return (
                    <motion.div
                        key={idx}
                        layout
                        initial={{ borderRadius: 12 }}
                        onClick={() => toggle(idx)}
                        className={cn(
                            "cursor-pointer overflow-hidden relative border",
                            isOpen ? "shadow-xl border-transparent" : "hover:bg-black/5 border-black/5"
                        )}
                        style={{
                            backgroundColor: isOpen ? cardBackgroundColor : 'transparent',
                            borderRadius: 16
                        }}
                    >
                        {isOpen && (
                            <div className="absolute top-0 right-0 w-1 h-full" style={{ backgroundColor: finalAccentColor }}></div>
                        )}

                        <div className="p-5 flex items-center justify-between">
                            <div className="flex items-center gap-5">
                                <div
                                    className={cn("w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors")}
                                    style={{
                                        backgroundColor: isOpen ? `${finalAccentColor}15` : 'rgba(0,0,0,0.05)',
                                        color: isOpen ? finalAccentColor : finalIconColor
                                    }}
                                >
                                    <IconRenderer name={item.icon} className="w-6 h-6" />
                                </div>
                                <RichTextRenderer
                                    html={item.question}
                                    className={cn("text-lg transition-all", isOpen ? "font-extrabold text-xl" : "font-semibold")}
                                    style={{ color: finalTextColor }}
                                    inline
                                />
                            </div>
                            <div
                                className={cn("w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300")}
                                style={{
                                    borderColor: isOpen ? finalAccentColor : 'rgba(0,0,0,0.1)',
                                    backgroundColor: isOpen ? finalAccentColor : 'transparent',
                                    color: isOpen ? '#fff' : finalIconColor
                                }}
                            >
                                <ChevronDown className={cn("w-4 h-4", isOpen && "rotate-180")} />
                            </div>
                        </div>

                        <AnimatePresence>
                            {isOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                >
                                    <div className="px-5 pb-6 pt-2 mr-[68px]">
                                        <RichTextRenderer html={item.answer} className="leading-relaxed text-lg" style={{ color: finalTextColor }} />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                );
            })}
        </div>
    );


    // Main Render Switch
    const renderContent = () => {
        switch (variant) {
            case 'grid_cards': return renderGridCards();
            case 'side_panel':
                return (
                    <>
                        <div className="block md:hidden">{renderClassicAccordion()}</div>
                        <div className="hidden md:block">{renderSidePanel()}</div>
                    </>
                );
            case 'highlight_hero': return renderHighlightHero();
            case 'classic_accordion':
            default: return renderClassicAccordion();
        }
    };

    return (
        <section className={cn("w-full py-12", className)} style={{ backgroundColor, ...style }} dir="rtl">
            <div className="w-full px-4 md:px-0 container mx-auto">
                <div className="text-center mb-12 max-w-3xl mx-auto">
                    {title && <div className="text-3xl md:text-4xl font-bold mb-4 [&_p]:m-0" style={{ color: finalTextColor }} dangerouslySetInnerHTML={{ __html: title }} />}
                    {description && <div className="opacity-80 text-lg leading-relaxed" style={{ color: finalTextColor }} dangerouslySetInnerHTML={{ __html: description }} />}
                </div>

                {renderContent()}
            </div>

            {/* JSON-LD Schema for SEO */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": items.map(item => ({
                            "@type": "Question",
                            "name": item.question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": item.answer
                            }
                        }))
                    })
                }}
            />
        </section>
    );
};

